import type {
  Material,
  MaterialAlphaMode,
  MaterialKind,
  MaterialUUID,
  TextureUUID,
  Vector4,
} from "../../types/scene";
import { create } from "mutative";
import { withHistory } from "./withHistory";
import { createDefaultPhongMaterial, normalizeMaterialColor } from "../../utils/materialDefaults";
import { generateUuid } from "../../utils/id";
import type { SceneStoreSetState, SceneStoreGetState } from "../sceneStore.types";
import type { MaterialActions } from "./actionTypes";
import { shouldBlockSceneAuthoringMutation } from "../../utils/physicsGuards";
import { logger } from "../../utils/logger";

const ensureMaterialUuid = (material: Material): Material => {
  if (material.uuid) return material;
  return { ...material, uuid: generateUuid() as MaterialUUID };
};

type MaterialNormalizationResult = {
  materials: Record<string, Material>;
  keyMap: Record<string, string>;
};

type LegacyMaterialInput = Partial<Material> & {
  map?: unknown;
  texture?: unknown;
  rgba?: unknown;
  type?: unknown;
  metallic?: unknown;
  roughnessValue?: unknown;
};

type MutableMaterialShape = Partial<Material> & {
  uuid?: MaterialUUID;
  name?: string;
  kind?: MaterialKind;
  color?: Vector4 | number[];
  colorMap?: TextureUUID | string;
  alphaMap?: TextureUUID | string;
  alphaMode?: MaterialAlphaMode;
  alphaCutoff?: unknown;
  doubleSided?: unknown;
  transparent?: boolean;
  opacity?: number;
  metalness?: unknown;
  metallic?: unknown;
  roughness?: unknown;
  roughnessValue?: unknown;
  shininess?: unknown;
  clearcoat?: unknown;
  clearcoatRoughness?: unknown;
  transmission?: unknown;
  thickness?: unknown;
  ior?: unknown;
  specularIntensity?: unknown;
  specularColor?: unknown;
  anisotropy?: unknown;
  iridescence?: unknown;
};

type BodyMaterialRefs = {
  geoms?: Array<{
    materials?: string[];
  }>;
};

const normalizeMaterialRecord = (
  materials?: Record<string, Partial<Material> | Material>
): MaterialNormalizationResult => {
  const normalized: Record<string, Material> = {};
  const keyMap: Record<string, string> = {};

  Object.entries(materials || {}).forEach(([inputKey, material]) => {
    if (!material) return;

    const resolvedName = material.name || inputKey;
    const kind = inferMaterialKind(material as Partial<Material>);
    const base =
      material && (material as Material).kind
        ? ensureMaterialUuid({ ...(material as Material) })
        : createBaseMaterialForKind(resolvedName, kind);

    const shaped = ensureMaterialShape(resolvedName, kind, base, material as Partial<Material>);

    const finalMaterial = ensureMaterialUuid(shaped);
    normalized[finalMaterial.uuid] = finalMaterial;
    keyMap[inputKey] = finalMaterial.uuid;
    if (resolvedName && !keyMap[resolvedName]) {
      keyMap[resolvedName] = finalMaterial.uuid;
    }
  });

  return { materials: normalized, keyMap };
};

const stripLegacyMaterialProps = (input?: Partial<Material> | Material) => {
  if (!input) return {};
  const {
    map: _map,
    texture: _texture,
    rgba: _rgba,
    type: _type,
    metallic: _metallic,
    roughnessValue: _roughnessValue,
    ...rest
  } = input as LegacyMaterialInput;
  return rest;
};

const toFiniteNumber = (...candidates: unknown[]): number | undefined => {
  for (const value of candidates) {
    if (value === null || value === undefined) continue;
    const numeric = typeof value === "string" ? Number(value) : (value as number);
    if (Number.isFinite(numeric)) {
      return numeric;
    }
  }
  return undefined;
};

const clamp01 = (value: unknown, fallback: number): number => {
  const numeric = typeof value === "string" ? Number(value) : (value as number);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(0, Math.min(1, numeric));
};

const inferMaterialKind = (material?: Partial<Material>): MaterialKind => {
  const maybeKind = material?.kind;
  if (maybeKind === "standard" || maybeKind === "physical" || maybeKind === "phong") {
    return maybeKind;
  }

  const candidate = material as Record<string, unknown> | undefined;
  if (!candidate) {
    return "phong";
  }

  const hasPhysicalSignals =
    [
      "clearcoat",
      "clearcoatRoughness",
      "transmission",
      "thickness",
      "specularIntensity",
      "anisotropy",
      "iridescence",
    ].some((key) => {
      const value = candidate[key];
      return typeof value === "number" && value > 0;
    }) ||
    (candidate.ior && typeof candidate.ior === "number" && candidate.ior !== 1.45) ||
    (candidate.specularColor && Array.isArray(candidate.specularColor));
  if (hasPhysicalSignals) {
    return "physical";
  }

  const hasPBRSignals = ["metalness", "metallic", "roughness", "roughnessValue"].some(
    (key) => candidate[key] !== undefined
  );
  if (hasPBRSignals) {
    return "standard";
  }

  const hasPhongSignals = ["specular", "shininess"].some((key) => candidate[key] !== undefined);
  if (hasPhongSignals) {
    return "phong";
  }

  return "phong";
};

const createBaseMaterialForKind = (name: string, kind: MaterialKind): Material => {
  if (kind === "phong") {
    return ensureMaterialUuid(createDefaultPhongMaterial(name));
  }

  return ensureMaterialUuid({
    kind,
    name,
  } as Material);
};

const ensureMaterialShape = (
  materialName: string,
  kind: MaterialKind,
  base: Material,
  updates?: Partial<Material>
) => {
  const baseShape = stripLegacyMaterialProps(base) as Partial<Material>;
  const strippedUpdates = stripLegacyMaterialProps(updates) as Partial<Material>;
  const next = {
    ...baseShape,
    ...strippedUpdates,
  } as Material;
  next.name = materialName;
  next.kind = kind;

  const nextShape = next as MutableMaterialShape;
  const updateShape = updates as MutableMaterialShape | undefined;
  const baseShapeForConversion = base as MutableMaterialShape;

  if (updates?.color) {
    next.color = normalizeMaterialColor(updates.color);
  }

  const normalizedColor = normalizeMaterialColor(next.color);
  next.color = normalizedColor;

  if (updates?.opacity == null) {
    next.opacity = next.opacity ?? normalizedColor[3];
  }
  if (updates?.transparent == null) {
    next.transparent = next.transparent ?? (next.opacity ?? normalizedColor[3]) < 1;
  }

  if (updates?.opacity == null && updates?.color) {
    next.opacity = normalizedColor[3];
  }
  if (updates?.transparent == null && updates?.color) {
    next.transparent = (next.opacity ?? normalizedColor[3]) < 1;
  }

  if (updates?.opacity != null && updates.transparent == null) {
    next.transparent = updates.opacity < 1;
  }

  const hasAlphaTexture =
    typeof nextShape.alphaMap === "string" ||
    (typeof nextShape.colorMap === "string" && next.transparent === true);
  const alphaMode = updateShape?.alphaMode ?? nextShape.alphaMode;
  if (alphaMode === "opaque" || alphaMode === "blend" || alphaMode === "mask") {
    nextShape.alphaMode = alphaMode;
  } else {
    nextShape.alphaMode = next.transparent || hasAlphaTexture ? "blend" : "opaque";
  }
  nextShape.alphaCutoff = clamp01(nextShape.alphaCutoff, 0.5);
  nextShape.doubleSided = typeof nextShape.doubleSided === "boolean" ? nextShape.doubleSided : true;
  next.transparent = nextShape.alphaMode === "blend" || (next.opacity ?? 1) < 1;

  if (kind === "standard" || kind === "physical") {
    const resolvedMetalness = toFiniteNumber(
      updateShape?.metalness,
      updateShape?.metallic,
      nextShape.metalness
    );
    const resolvedRoughness = toFiniteNumber(
      updateShape?.roughness,
      updateShape?.roughnessValue,
      nextShape.roughness
    );

    // Clamp metalness and roughness to valid 0-1 range, but allow intermediate values
    nextShape.metalness =
      resolvedMetalness !== undefined ? Math.max(0, Math.min(1, resolvedMetalness)) : 0;
    nextShape.roughness =
      resolvedRoughness !== undefined ? Math.max(0, Math.min(1, resolvedRoughness)) : 0.5;
    delete nextShape.metallic;
    delete nextShape.roughnessValue;

    // If converting from Phong-style inputs, prefer non-metal and derive roughness from shininess
    const shininessCandidate = toFiniteNumber(
      baseShapeForConversion.shininess,
      updateShape?.shininess,
      nextShape.shininess
    );
    if (kind === "standard" && shininessCandidate !== undefined) {
      // Convert Blinn-Phong shininess to GGX roughness approximation
      const roughFromShine = Math.sqrt(2 / (Number(shininessCandidate) + 2));
      if (resolvedRoughness === undefined) {
        nextShape.roughness = Math.max(0, Math.min(1, roughFromShine));
      }
      // Default metalness to 0 when originating from Phong parameters
      if (resolvedMetalness === undefined) {
        nextShape.metalness = 0;
      }
    }

    if (kind !== "physical") {
      // Remove physics properties for non-physical materials
      delete nextShape.clearcoat;
      delete nextShape.clearcoatRoughness;
      delete nextShape.transmission;
      delete nextShape.thickness;
      delete nextShape.ior;
      delete nextShape.specularIntensity;
      delete nextShape.specularColor;
      delete nextShape.anisotropy;
      delete nextShape.iridescence;
    }
  } else {
    // Ensure phong materials do not carry PBR-only fields
    delete nextShape.metalness;
    delete nextShape.roughness;
    // Also remove physics properties for phong materials
    delete nextShape.clearcoat;
    delete nextShape.clearcoatRoughness;
    delete nextShape.transmission;
    delete nextShape.thickness;
    delete nextShape.ior;
    delete nextShape.specularIntensity;
    delete nextShape.specularColor;
    delete nextShape.anisotropy;
    delete nextShape.iridescence;
  }

  if (
    updates &&
    Object.prototype.hasOwnProperty.call(updates, "colorMap") &&
    updates.colorMap == null
  ) {
    delete next.colorMap;
  }

  const candidateUuid = (updates as Material | undefined)?.uuid ?? base.uuid ?? next.uuid;
  next.uuid = candidateUuid || (generateUuid() as MaterialUUID);

  return next;
};

/**
 * Remaps material references in all body geoms.
 * Works directly with flat bodies structure - no tree traversal needed.
 *
 * @param bodiesMap - Flat map of bodies by UUID
 * @param mapping - Map of old material UUIDs to new material UUIDs
 */
const remapBodiesMaterials = (
  bodiesMap: Record<string, BodyMaterialRefs> | undefined,
  mapping: Record<string, string>
) => {
  if (!bodiesMap || typeof bodiesMap !== "object" || Object.keys(mapping).length === 0) return;

  Object.values(bodiesMap).forEach((body) => {
    if (!body) return;
    if (Array.isArray(body.geoms)) {
      body.geoms.forEach((geom) => {
        if (!geom || !Array.isArray(geom.materials)) return;
        geom.materials = geom.materials.map((matKey) => mapping[matKey] ?? matKey);
      });
    }
  });
};

const ensureDefaultMaterialEntry = (materials: Record<string, Material>) => {
  const existing = Object.entries(materials).find(
    ([, mat]) => mat?.name && mat.name === "default_mat"
  );
  if (existing) {
    return { id: existing[0], material: existing[1] };
  }

  const generated = {
    ...createDefaultPhongMaterial("default_mat", {
      color: [0.75, 0.75, 0.75, 1],
    }),
    autoGenerated: true,
  } as Material & { autoGenerated: boolean };
  materials[generated.uuid] = generated;
  return { id: generated.uuid, material: generated };
};

export const createMaterialActions = (
  set: SceneStoreSetState,
  get: SceneStoreGetState
): MaterialActions => ({
  loadMaterials: (materials: Record<string, Material>) => {
    if (shouldBlockSceneAuthoringMutation("loadMaterials")) return {};
    let returnedKeyMap: Record<string, MaterialUUID> = {};
    set((state) =>
      create(state, (draft) => {
        // Merge with existing materials instead of replacing them
        const existingMaterials = draft.materials;
        const mergedInput = { ...existingMaterials, ...materials };
        const { materials: normalized, keyMap } = normalizeMaterialRecord(mergedInput);
        returnedKeyMap = keyMap as Record<string, MaterialUUID>;
        draft.materials = normalized;
        // Ensure default_mat exists if it doesn't already
        ensureDefaultMaterialEntry(draft.materials);
        // Direct access to flat bodies map - no tree reconstruction needed
        remapBodiesMaterials(draft.bodies, keyMap);
      })
    );
    return returnedKeyMap;
  },

  addMaterial: withHistory((material: Material) =>
    set((state) =>
      create(state, (draft) => {
        const name = material.name || "material";
        const kind = inferMaterialKind(material);
        const base =
          material && material.kind
            ? ensureMaterialUuid({ ...material })
            : createBaseMaterialForKind(name, kind);
        const shaped = ensureMaterialShape(name, kind, base, material);
        const final = ensureMaterialUuid(shaped);
        draft.materials[final.uuid] = final;
      })
    )
  ),

  updateMaterial: withHistory((materialId: string, updates: Partial<Material>) =>
    set((state) => {
      const materials = state.materials as Record<string, Material>;
      const material = materials[materialId];
      if (!material) {
        logger.warn("[MaterialActions] Material not found:", materialId);
        return state;
      }

      const result = create(state, (draft) => {
        const draftMaterials = draft.materials as Record<string, Material>;
        const existing: Material = draftMaterials[materialId];
        const nextKind = inferMaterialKind({ ...existing, ...updates });
        const shaped = ensureMaterialShape(
          existing.name || existing.uuid,
          nextKind,
          existing,
          updates
        );
        const final = ensureMaterialUuid({ ...shaped, uuid: existing.uuid });
        draftMaterials[materialId] = final;
      });

      setTimeout(() => {
        const latest = get().materials?.[materialId as MaterialUUID] ?? material;
        window.dispatchEvent(
          new CustomEvent("material-updated", {
            detail: {
              materialId,
              materialName: latest?.name,
              updates,
            },
          })
        );
      }, 0);

      return result;
    })
  ),

  deleteMaterial: withHistory((materialId: string) =>
    set((state) =>
      create(state, (draft) => {
        const materials: Record<string, Material> = draft.materials;
        if (!materials[materialId]) return;

        const { id: defaultId } = ensureDefaultMaterialEntry(materials);
        let fallbackId = defaultId;

        if (materialId === defaultId) {
          const replacement = {
            ...createDefaultPhongMaterial("default_mat", {
              color: [0.75, 0.75, 0.75, 1],
            }),
            autoGenerated: true,
          } as Material & { autoGenerated: boolean };
          materials[replacement.uuid] = replacement;
          fallbackId = replacement.uuid;
        }

        delete materials[materialId];
        // Direct access to flat bodies map - no tree reconstruction needed
        remapBodiesMaterials(draft.bodies, { [materialId]: fallbackId });
      })
    )
  ),

  renameMaterial: withHistory((materialId: string, newName: string) =>
    set((state) => {
      if (!materialId || typeof newName !== "string" || !newName.trim()) return state;
      const materials = state.materials as Record<string, Material>;
      if (!materials[materialId]) return state;

      return create(state, (draft) => {
        const draftMaterials = draft.materials as Record<string, Material>;
        const material = draftMaterials[materialId];
        if (!material) return;
        material.name = newName.trim();
      });
    })
  ),
});
